import { __assign, __spreadArrays } from "tslib";
import { getEventPath, getShadowElement, isDefined, isNumber, Platform } from '@tyler-components-web/core';
import { STEP_CONSTANTS } from '../step/step-constants';
import { StepperUtils } from '../core/stepper-utils';
import { STEPPER_CONSTANTS } from './stepper-constants';
var StepperFoundation = /** @class */ (function () {
    function StepperFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._steps = [];
        this._selectedIndex = 0;
        this._linear = false;
        this._alternative = false;
        this._layoutMode = 'fixed';
        this._layoutAlign = 'center';
        this._initialize = false;
        this._clickListener = function (event) { return _this._handleClick(event); };
        this._keyListener = function (event) { return _this._onKeydown(event); };
        this._stepFocusListener = function () { return _this._onStepFocus(); };
        this._stepBlurListener = function () { return _this._onStepBlur(); };
        this._stepExpandedContentFocusInListener = function (event) { return _this._onStepExpandedContentFocusIn(event); };
        this._stepExpandedContentFocusOutListener = function (event) { return _this._onStepExpandedContentFocusOut(event); };
        this._slotChangeListener = function (event) { return _this._onSlotChange(event); };
    }
    StepperFoundation.prototype.initialize = function () {
        this._adapter.assignIndices();
        this._adapter.setLinearState(this._linear);
        this._adapter.setAlternativeState(this._alternative);
        this._adapter.setLayoutAlign(this._layoutAlign);
        this._adapter.setLayoutMode(this._layoutMode);
        this._adapter.addRootListener('click', this._clickListener);
        this._adapter.addHostListener('keydown', this._keyListener);
        this._adapter.assignFirstLastStep();
        this._adapter.addRootListener(STEP_CONSTANTS.events.EXPANDED_CONTENT_FOCUSIN, this._stepExpandedContentFocusInListener);
        this._adapter.addRootListener(STEP_CONSTANTS.events.EXPANDED_CONTENT_FOCUSOUT, this._stepExpandedContentFocusOutListener);
        this._applySelectedIndex();
        this._patchBrowser();
        this._adapter.setStepsListener('blur', this._stepBlurListener);
        this._adapter.setStepsListener('focus', this._stepFocusListener);
        if (!this._initialize) {
            this._adapter.addSlotChangeListener(this._slotChangeListener);
        }
        this._initialize = true;
    };
    StepperFoundation.prototype.disconnect = function () {
        this._adapter.removeRootListener('click', this._clickListener);
        this._adapter.removeHostListener('keydown', this._keyListener);
        this._adapter.removeSlotChangeListener(this._slotChangeListener);
        this._adapter.removeStepsListener('blur', this._stepBlurListener);
        this._adapter.removeStepsListener('focus', this._stepFocusListener);
    };
    Object.defineProperty(StepperFoundation.prototype, "steps", {
        /** The step configurations. */
        get: function () {
            return JSON.parse(JSON.stringify(this._steps));
        },
        set: function (value) {
            if (Array.isArray(value) && value.length > 0) {
                this._steps = __spreadArrays(value.map(function (s) { return (__assign({}, s)); }));
            }
            else {
                this._steps = [];
                return;
            }
            this._renderConfiguration();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperFoundation.prototype, "selectedIndex", {
        /** The active step index. */
        get: function () {
            return this._selectedIndex;
        },
        set: function (value) {
            if (!isDefined(value)) {
                value = 0;
            }
            else if (!isNumber(value)) {
                var v = Number(value);
                value = !isNaN(v) ? v : 0;
            }
            if (this._selectedIndex !== value) {
                this._selectedIndex = value;
                this._adapter.setHostAttribute(STEPPER_CONSTANTS.attributes.SELECTED_INDEX, this._selectedIndex.toString());
                this._applySelectedIndex();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperFoundation.prototype, "linear", {
        /** Whether the stepper is linear or non-linear. */
        get: function () {
            return this._linear;
        },
        set: function (value) {
            value = Boolean(value);
            if (this._linear !== value) {
                this._linear = value;
                this._adapter.setLinearState(this._linear);
                this._adapter.setHostAttribute(STEPPER_CONSTANTS.attributes.LINEAR, this._linear.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperFoundation.prototype, "alternative", {
        /** Whether the stepper uses the default or alternative label layout mode. */
        get: function () {
            return this._alternative;
        },
        set: function (value) {
            value = Boolean(value);
            if (this._alternative !== value) {
                this._alternative = value;
                this._adapter.setAlternativeState(this._alternative);
                this._adapter.setHostAttribute(STEPPER_CONSTANTS.attributes.ALTERNATIVE, this._alternative.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperFoundation.prototype, "layoutMode", {
        /** The layout mode of the stepper. */
        get: function () {
            return this._layoutMode;
        },
        set: function (value) {
            if (!/^(fixed|clustered)$/.test(value)) {
                return;
            }
            if (this._layoutMode !== value) {
                this._layoutMode = value;
                this._adapter.setHostAttribute(STEPPER_CONSTANTS.attributes.LAYOUT_MODE, this._layoutMode);
                this._adapter.setLayoutMode(this._layoutMode);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperFoundation.prototype, "layoutAlign", {
        /** The layout alignment of the stepper. */
        get: function () {
            return this._layoutAlign;
        },
        set: function (value) {
            if (!/^(left|center|right)$/.test(value)) {
                return;
            }
            if (this._layoutAlign !== value) {
                this._layoutAlign = value;
                this._adapter.setLayoutAlign(this._layoutAlign);
                this._adapter.setHostAttribute(STEPPER_CONSTANTS.attributes.LAYOUT_ALIGN, this._layoutAlign);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperFoundation.prototype, "disabled", {
        get: function () {
            return this._disabled;
        },
        set: function (value) {
            if (value !== this._disabled) {
                this._disabled = value;
                this._applyDisabled();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(StepperFoundation.prototype, "vertical", {
        get: function () {
            return this._vertical;
        },
        set: function (value) {
            if (value !== this._vertical) {
                this._vertical = value;
                this._applyVertical();
                this._adapter.toggleHostAttribute(STEPPER_CONSTANTS.attributes.VERTICAL, this._vertical);
            }
        },
        enumerable: true,
        configurable: true
    });
    StepperFoundation.prototype._applyVertical = function () {
        this._adapter.setVertical(this._vertical);
    };
    StepperFoundation.prototype._applyDisabled = function () {
        if (this._disabled) {
            this._adapter.setHostAttribute(STEPPER_CONSTANTS.attributes.DISABLED, 'disabled');
        }
        else {
            this._adapter.removeHostAttribute(STEPPER_CONSTANTS.attributes.DISABLED);
        }
        this._adapter.toggleDisabled(this._disabled);
    };
    StepperFoundation.prototype._applySelectedIndex = function () {
        this._adapter.setSelectedByIndex(this._selectedIndex);
    };
    StepperFoundation.prototype._handleClick = function (event) {
        event.preventDefault();
        if (this._linear) {
            return;
        }
        var eventPath = getEventPath(event);
        var stepRoot = eventPath.find(function (el) { return el.classList && el.classList.contains(STEP_CONSTANTS.classes.STEP); });
        var step = eventPath.find(function (el) { return el.localName === STEP_CONSTANTS.elementName; });
        if (stepRoot && step && !step.selected && !step.disabled && this._adapter.emitHostEvent(STEP_CONSTANTS.events.SELECTED, step.index, true, true)) {
            this._adapter.setSelected(step);
            this.selectedIndex = step.index;
        }
    };
    StepperFoundation.prototype._renderConfiguration = function () {
        var steps = StepperUtils.createSteps({
            steps: this._steps,
            selectedIndex: this._selectedIndex,
            alternative: this._alternative,
            layoutAlign: this._layoutAlign,
            layoutMode: this._layoutMode,
            linear: this._linear
        });
        this.disconnect();
        this._adapter.applyConfiguredSteps(steps);
        this.initialize();
    };
    StepperFoundation.prototype._onKeydown = function (event) {
        if (this._linear) {
            return;
        }
        var key = this._getKeyFromEvent(event);
        // If the expanded content is currently being focused, ignore stepper specific keys
        if (!this._adapter.isStepperFocused()) {
            return;
        }
        // Early exit if the event key isn't one of the keyboard navigation keys
        if (!key) {
            return;
        }
        if ([STEP_CONSTANTS.strings.HOME_KEY, STEP_CONSTANTS.strings.END_KEY, STEP_CONSTANTS.strings.ARROW_DOWN_KEY, STEPPER_CONSTANTS.strings.ARROW_UP_KEY].includes(key)) {
            event.preventDefault();
        }
        var step = this._vertical ? this._determineVerticalMoveTarget(key) : this._determineMoveTarget(key);
        if (step) {
            this._moveFocusTo(step);
        }
    };
    StepperFoundation.prototype._moveFocusTo = function (step) {
        getShadowElement(step, STEP_CONSTANTS.selectors.STEP).focus();
    };
    StepperFoundation.prototype._getKeyFromEvent = function (evt) {
        if (STEPPER_CONSTANTS.ACCEPTABLE_KEYS.includes(evt.key)) {
            return evt.key;
        }
        return STEPPER_CONSTANTS.KEYCODE_MAP[evt.keyCode];
    };
    StepperFoundation.prototype._determineMoveTarget = function (key) {
        var step = this._adapter.getFocusedOrSelectedStep();
        if (step) {
            switch (key) {
                case STEPPER_CONSTANTS.strings.ARROW_RIGHT_KEY:
                    return this._adapter.getStep(step.index + 1);
                case STEPPER_CONSTANTS.strings.ARROW_LEFT_KEY:
                    return this._adapter.getStep(step.index - 1);
                case STEPPER_CONSTANTS.strings.HOME_KEY:
                    return this._adapter.getStep(0);
                case STEPPER_CONSTANTS.strings.END_KEY:
                    return this._adapter.getLastStep();
            }
        }
        return step;
    };
    StepperFoundation.prototype._determineVerticalMoveTarget = function (key) {
        var step = this._adapter.getFocusedOrSelectedStep();
        if (step) {
            switch (key) {
                case STEPPER_CONSTANTS.strings.ARROW_DOWN_KEY:
                    return this._adapter.getStep(step.index + 1);
                case STEPPER_CONSTANTS.strings.ARROW_UP_KEY:
                    return this._adapter.getStep(step.index - 1);
                case STEPPER_CONSTANTS.strings.HOME_KEY:
                    return this._adapter.getStep(0);
                case STEPPER_CONSTANTS.strings.END_KEY:
                    return this._adapter.getLastStep();
            }
        }
        return step;
    };
    StepperFoundation.prototype._onSlotChange = function (event) {
        this.disconnect();
        this.initialize();
    };
    StepperFoundation.prototype._onStepExpandedContentFocusIn = function (event) {
        var step = this._adapter.getStep(event.detail.index + 1);
        if (step) {
            step.setStepTabIndex(0);
        }
        event.detail.setStepTabIndex(0);
    };
    StepperFoundation.prototype._onStepExpandedContentFocusOut = function (event) {
        var focusOutStep = event.target;
        var step = this._adapter.getStep(focusOutStep.index + 1);
        if (step) {
            step.setStepTabIndex(-1);
        }
        focusOutStep.setStepTabIndex(-1);
    };
    StepperFoundation.prototype._onStepFocus = function () {
        this._adapter.toggleRootClass(STEPPER_CONSTANTS.classes.FOCUSED, true);
    };
    StepperFoundation.prototype._onStepBlur = function () {
        this._adapter.toggleRootClass(STEPPER_CONSTANTS.classes.FOCUSED, false);
    };
    StepperFoundation.prototype._patchBrowser = function () {
        if (new Platform().SAFARI()) {
            this._adapter.patchSafari();
        }
    };
    return StepperFoundation;
}());
export { StepperFoundation };
